<script>
import { mapGetters, mapActions } from 'vuex'
import { getContrastingTextColor } from '@chatwoot/utils'
import nextAvailabilityTime from 'widget/mixins/nextAvailabilityTime'
import configMixin from 'widget/mixins/configMixin'
import availabilityMixin from 'widget/mixins/availability'
import { IFrameHelper } from 'widget/helpers/utils'
import { CHATWOOT_ON_START_CONVERSATION } from '../constants/sdkEvents'
import GroupedAvatars from 'widget/components/GroupedAvatars.vue'
import ResizableTextArea from 'shared/components/ResizableTextArea.vue'
import ChatSendButton from 'widget/components/ChatSendButton.vue'

export default {
  name: 'TeamAvailability',
  components: {
    GroupedAvatars,
    ResizableTextArea,
    ChatSendButton
  },
  mixins: [configMixin, nextAvailabilityTime, availabilityMixin],
  props: {
    availableAgents: {
      type: Array,
      default: () => { }
    },
    hasConversation: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      userInput: '',
      isFocused: false
    }
  },
  emits: ['startConversation'],

  computed: {
    ...mapGetters({
      widgetColor: 'appConfig/getWidgetColor',
      initMessage: 'appConfig/getInitMessage'
    }),
    textColor () {
      return getContrastingTextColor(this.widgetColor)
    },
    agentAvatars () {
      return this.availableAgents.map((agent) => ({
        name: agent.name,
        avatar: agent.avatar_url,
        id: agent.id
      }))
    },
    isOnline () {
      const { workingHoursEnabled } = this.channelConfig
      const anyAgentOnline = this.availableAgents.length > 0

      if (workingHoursEnabled) {
        return this.isInBetweenTheWorkingHours
      }
      return anyAgentOnline
    }
  },
  methods: {
    ...mapActions({
      setInitMessage: 'appConfig/setInitMessage',
      clearInitMessage: 'appConfig/clearInitMessage'
    }),
    startConversation () {
      this.$emit('startConversation')
      if (!this.hasConversation) {
        IFrameHelper.sendMessage({
          event: 'onEvent',
          eventIdentifier: CHATWOOT_ON_START_CONVERSATION,
          data: { hasConversation: false }
        })
      }
    },
    handleButtonClick () {
      if (this.userInput && this.userInput.trim()) {
        this.setInitMessage(this.userInput)
      }
      this.userInput = ''
      this.startConversation()
    },
    handleEnterKeyPress (e) {
      if (e.keyCode === 13 && !e.shiftKey) {
        e.preventDefault()
        this.handleButtonClick()
      }
    }
  },
  unmounted () {
    document.removeEventListener('keypress', this.handleEnterKeyPress)
  },
  mounted () {
    if (this.initMessage) {
      this.userInput = this.initMessage
    }
    document.addEventListener('keypress', this.handleEnterKeyPress)
  }
}
</script>

<template>
  <div>
    <template v-if="hasConversation">
      <div
        class="flex flex-col gap-3 w-full shadow outline-1 outline outline-n-container rounded-xl bg-n-background dark:bg-n-solid-2 px-5 py-4">
        <!-- <div v-if="isOnline" class="flex items-center justify-between gap-2">
          <div class="flex flex-col gap-1">
            <div class="font-medium text-n-slate-12">
              {{
                isOnline
                  ? $t('TEAM_AVAILABILITY.ONLINE')
                  : $t('TEAM_AVAILABILITY.OFFLINE')
              }}
            </div>
            <div class="text-n-slate-11">
              {{ replyWaitMessage }}
            </div>
          </div>
          <GroupedAvatars v-if="isOnline" :users="availableAgents" />
        </div> -->
        <button class="inline-flex items-center gap-1 font-medium text-n-slate-12" :style="{ color: widgetColor }"
          @click="startConversation">
          <span>
            {{ hasConversation ? $t('CONTINUE_CONVERSATION') : $t('START_CONVERSATION') }}
          </span>
          <i class="i-lucide-chevron-right size-5 mt-px" />
        </button>
      </div>
    </template>
    <template v-else>
      <div
        class="items-center flex ltr:pl-3 rtl:pr-3 ltr:pr-2 rtl:pl-2 rounded-[7px] transition-all duration-200 bg-n-background !shadow-[0_0_0_1px,0_0_2px_3px]"
        :class="{
          'focused-input': isFocused,
          '!shadow-n-strong dark:!shadow-n-strong': !isFocused
        }" :style="{ '--widget-color': widgetColor }">
        <ResizableTextArea id="chat-input" ref="chatInput" v-model="userInput" :rows="1"
          :aria-label="$t('CHAT_PLACEHOLDER')" :placeholder="$t('CHAT_PLACEHOLDER')"
          class="user-message-input reset-base" @focus="isFocused = true" @blur="isFocused = false" />
        <div class="flex items-center ltr:pl-2 rtl:pr-2">
          <ChatSendButton :color="widgetColor" @click="handleButtonClick" />
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.emoji-dialog {
  @apply max-w-full ltr:right-5 rtl:right-[unset] rtl:left-5 -top-[302px] before:ltr:right-2.5 before:rtl:right-[unset] before:rtl:left-2.5;
}

.user-message-input {
  @apply border-none outline-none w-full placeholder:text-n-slate-10 resize-none h-8 min-h-8 max-h-60 py-1 px-0 my-2 bg-n-background text-n-slate-12 transition-all duration-200;
}

.focused-input {
  box-shadow:
    rgba(0, 0, 0, 0) 0px 0px 0px 0px,
    rgba(0, 0, 0, 0) 0px 0px 0px 0px,
    var(--widget-color) 0px 0px 0px 1px,
    var(--widget-color) 0px 0px 2px 3px !important;
}
</style>
